import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiX,
  FiImage,
  FiChevronLeft,
  FiSearch,
  FiDownload,
} from "react-icons/fi";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { getAllReferralIncome } from "../../store/slices/withdraw";

const ReferralIncome = () => {
  const dispatch = useDispatch();

  const [selectedImage, setSelectedImage] = useState(null);


  const {
    referralIncome = [],
    referralIncomeLoading,
    referralIncomeError,
    referralIncomePagination,
  } = useSelector((state) => state.withdraw);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const totalIncome =
    referralIncomePagination?.totalIncome ||
    referralIncomePagination?.total ||
    0;

  const totalPages =
    referralIncomePagination?.totalPages ||
    Math.ceil(totalIncome / limit);

  const currentPage =
    referralIncomePagination?.currentPage || page;

  const hasNextPage =
    referralIncomePagination?.hasNextPage ||
    page < totalPages;

  const hasPreviousPage =
    referralIncomePagination?.hasPreviousPage ||
    page > 1;


  useEffect(() => {
    dispatch(
      getAllReferralIncome({
        page,
        limit,
        search: search.trim(),
      })
    );
  }, [dispatch, page, limit, search]);
  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* =====================================================
     FORMAT DATE TIME
  ===================================================== */

  const formatDateTime = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* =====================================================
     CLOSE IMAGE VIEWER
  ===================================================== */

  const closeImageViewer = () => {
    setSelectedImage(null);
  };

  const handlePrevious = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== page
    ) {
      setPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          page - 1,
          page,
          page + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  const displayStart =
    totalIncome > 0
      ? (page - 1) * limit + 1
      : 0;

  const displayEnd =
    Math.min(page * limit, totalIncome);
  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      const response = await dispatch(
        getAllReferralIncome({
          page: 1,
          limit: 100000,
          search: search.trim(),
        })
      ).unwrap();

      const exportList =
        response?.data?.referralIncome ||
        response?.referralIncome ||
        [];

      if (!exportList.length) {
        alert("No referral income records available.");
        return;
      }

      const doc = new jsPDF(
        "landscape",
        "mm",
        "a4"
      );

      doc.setFontSize(16);

      doc.text(
        "Referral Income",
        14,
        15
      );

      if (search.trim()) {
        doc.setFontSize(9);

        doc.text(
          `Search: ${search.trim()}`,
          14,
          21
        );
      }

      const tableData = exportList.map(
        (item, index) => {

          const user =
            item?.userId || {};

          const fromUser =
            item?.fromUserId || {};

          const amount =
            Number(item?.amount || 0);

          return [
            index + 1,

            user?.name || "-",

            user?.userId || "-",

            fromUser?.userId || "-",

            fromUser?.name || "-",

            `$ ${amount.toLocaleString(
              "en-IN",
              {
                maximumFractionDigits: 2,
              }
            )}`,

            item?.remarks ||
            item?.incomeType ||
            "-",

            formatDate(
              item?.createdAt
            ),
          ];
        }
      );

      autoTable(doc, {
        startY: search.trim()
          ? 27
          : 22,

        head: [[
          "#",
          "Name",
          "User ID",
          "From User ID",
          "From User",
          "Amount",
          "Remarks",
          "Date",
        ]],

        body: tableData,

        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 3,
        },

        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 12,
          },
          1: {
            cellWidth: 35,
          },
          2: {
            cellWidth: 30,
          },
          3: {
            cellWidth: 30,
          },
          4: {
            cellWidth: 35,
          },
          5: {
            cellWidth: 28,
          },
          6: {
            cellWidth: 45,
          },
          7: {
            cellWidth: 30,
          },
        },
      });

      doc.save(
        "referral-income.pdf"
      );

    } catch (error) {

      console.error(
        "PDF download failed:",
        error
      );

      alert(
        "Failed to download PDF."
      );

    } finally {

      setDownloadLoading(false);

    }
  };

  return (
    <div className="gi-referral-page">

      {/* ================= BREADCRUMB ================= */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Referral Income
        </span>

      </div>

      {/* ================= TABLE ================= */}

      <section className="gi-referral-table-card">

        {/* ================= FILTER BAR ================= */}

        <div className="gi-users-filter">

          {/* SEARCH */}

          {/* PER PAGE */}

          <div className="gi-users-page-size me-2">
            <label htmlFor="pageSize">
              Show
            </label>

            <select
              id="pageSize"
              value={limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);

                setLimit(newLimit);
                setPage(1);
              }}
              disabled={referralIncomeLoading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span>
              per page
            </span>
          </div>

          <div className="gi-users-search me-2">
            <FiSearch />

            <input
              type="text"
              placeholder="Search referral income..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>



          {/* DOWNLOAD */}

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleDownloadPDF}
            disabled={
              downloadLoading ||
              referralIncomeLoading ||
              referralIncome.length === 0
            }
          >
            <FiDownload />

            <span>
              {downloadLoading
                ? "Downloading..."
                : "Download PDF"}
            </span>
          </button>

        </div>
        <div className="gi-referral-table-wrapper">

          <table className="gi-referral-table">

            <thead>

              <tr>

                <th>
                  #
                </th>

                <th>
                  Name
                </th>

                <th className="text-center">
                  User ID
                </th>

                <th className="text-center">
                  From User
                </th>


                <th className="text-center">
                  Amount
                </th>

                <th className="text-center">
                  Remarks
                </th>

                <th className="text-center">
                  Date
                </th>

              </tr>

            </thead>

            <tbody>

              {/* ================= LOADING ================= */}

              {referralIncomeLoading && (

                <tr>

                  <td
                    colSpan="8"
                    className="gi-table-message"
                  >
                    Loading payments...
                  </td>

                </tr>

              )}

              {/* ================= ERROR ================= */}

              {!referralIncomeLoading &&
                referralIncomeError && (

                  <tr>

                    <td
                      colSpan="8"
                      className="gi-table-message"
                    >
                      {referralIncomeError}
                    </td>

                  </tr>

                )}

              {/* ================= NO DATA ================= */}

              {!referralIncomeLoading &&
                !referralIncomeError &&
                referralIncome.length === 0 && (

                  <tr>

                    <td
                      colSpan="8"
                      className="gi-table-message"
                    >
                      No payments found
                    </td>

                  </tr>

                )}

              {/* ================= REFERRAL DATA ================= */}

              {!referralIncomeLoading &&
                !referralIncomeError &&
                referralIncome.map(
                  (item, index) => {

                    const user =
                      item?.userId || {};

                    const fromUser =
                      item?.fromUserId || {};

                    return (

                      <tr
                        key={
                          item?._id ||
                          index
                        }
                      >

                        {/* SERIAL */}

                        <td className="gi-referral-number">

                          {String(
                            (page - 1) * limit + index + 1
                          ).padStart(2, "0")}
                        </td>

                        {/* USER */}

                        <td>

                          <div className="gi-user-cell">

                            {user?.name ||
                              "-"}



                          </div>

                        </td>

                        {/* USER ID */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {user?.userId ||
                              "-"}

                          </span>

                        </td>

                        {/* FROM USER */}

                        <td className="text-center">
                          <span className="gi-user-id">

                            {fromUser?.userId ||
                              "-"}

                          </span><br />
                          <span className="gi-user-id">

                            {fromUser?.name ||
                              "-"}

                          </span>

                        </td>



                        {/* AMOUNT */}

                        <td className="text-center">

                          <strong className="gi-payment-amount">

                            ${" "}
                            {Number(
                              item?.amount || 0
                            ).toLocaleString(
                              "en-IN",
                              {
                                maximumFractionDigits: 2,
                              }
                            )}

                          </strong>

                        </td>

                        {/* REMARKS */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {item?.remarks ||
                              item?.incomeType ||
                              "-"}

                          </span>

                        </td>

                        {/* DATE */}

                        <td className="text-center">

                          <span className="gi-date-text">

                            {formatDate(
                              item?.createdAt
                            )}

                          </span>

                        </td>

                      </tr>

                    );
                  }
                )}

            </tbody>

          </table>
          {!referralIncomeLoading &&
            !referralIncomeError &&
            totalPages > 0 && (
              <div className="gi-users-pagination">

                {/* PAGINATION INFO */}

                <div className="gi-pagination-info">
                  Showing{" "}

                  <strong>
                    {displayStart}
                  </strong>

                  {" "}to{" "}

                  <strong>
                    {displayEnd}
                  </strong>

                  {" "}of{" "}

                  <strong>
                    {totalIncome}
                  </strong>

                  {" "}payments
                </div>


                {/* PAGINATION BUTTONS */}

                <div className="gi-pagination-buttons">

                  {/* PREVIOUS */}

                  <button
                    type="button"
                    className="gi-pagination-btn"
                    onClick={handlePrevious}
                    disabled={!hasPreviousPage}
                  >
                    <FiChevronLeft />

                    <span>
                      Prev
                    </span>
                  </button>


                  {/* PAGE NUMBERS */}

                  {getPageNumbers().map(
                    (pageNumber, index) => {

                      if (pageNumber === "...") {
                        return (
                          <span
                            key={`dots-${index}`}
                            className="gi-pagination-dots"
                          >
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          type="button"
                          key={pageNumber}
                          className={`gi-pagination-number ${page === pageNumber
                            ? "active"
                            : ""
                            }`}
                          onClick={() =>
                            handlePageChange(pageNumber)
                          }
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                  )}


                  {/* NEXT */}

                  <button
                    type="button"
                    className="gi-pagination-btn"
                    onClick={handleNext}
                    disabled={!hasNextPage}
                  >
                    <span>
                      Next
                    </span>

                    <FiChevronRight />
                  </button>

                </div>

              </div>
            )}

        </div>

      </section >

      {/* ================= IMAGE VIEWER ================= */}

      {
        selectedImage && (

          <div
            className="gi-image-viewer-overlay"
            onClick={closeImageViewer}
          >

            <div
              className="gi-image-viewer-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              {/* CLOSE */}

              <button
                type="button"
                className="gi-image-viewer-close"
                onClick={closeImageViewer}
              >

                <FiX />

              </button>

              {/* HEADER */}

              <div className="gi-image-viewer-header">

                <div className="gi-image-viewer-title">

                  <div className="gi-image-viewer-icon">

                    <FiImage />

                  </div>

                  <div>

                    <h3>
                      Payment Proof
                    </h3>

                    <p>
                      {selectedImage.userName ||
                        "User"}
                    </p>

                  </div>

                </div>

              </div>

              {/* IMAGE */}

              <div className="gi-image-viewer-body">

                <img
                  src={selectedImage.url}
                  alt="Payment Proof"
                />

              </div>

              {/* FOOTER */}

              <div className="gi-image-viewer-footer">

                <span>

                  Transaction ID:

                  <strong>
                    {selectedImage.transactionId ||
                      "-"}
                  </strong>

                </span>

              </div>

            </div>

          </div>

        )
      }

    </div >
  );
};

export default ReferralIncome;